import { format, parseISO } from 'date-fns'

import { formatCalendarDate } from '@snootsvet/shared'

import { DatePicker } from '@/frontend/components/date-picker'
import { useViewerFormat } from '@/frontend/components/formatted-datetime'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'
import type { TreatmentReminderTarget } from '@/frontend/features/patient/reminder-targets'

/**
 * The two fields that define a reminder — its target and its due date — as one controlled
 * pair. Shared by the vaccine treatment form (rows in TanStack Form array state, saved with
 * the treatment) and the inline add control (local state, POSTed on its own): the inputs are
 * identical, only what happens to the values differs, so the callers own the state and the
 * surrounding buttons.
 *
 * Renders a bare fragment so each caller keeps its own layout wrapper.
 */
export function ReminderFields({
  options,
  target,
  dueAt,
  anchor,
  onTargetChange,
  onDueAtChange,
}: {
  /**
   * Selectable diseases, already filtered by the caller to exclude covered targets
   * (`disease-list` regions, US). Omit when the target is fixed — `product-name` regions (UK)
   * use the vaccine's own name, so there's nothing to choose and `target` renders as a label.
   */
  options?: readonly TreatmentReminderTarget[]
  /** The current target. In the fixed case the caller passes the resolved vaccine name. */
  target: TreatmentReminderTarget | null
  dueAt: string | null
  /** Date the interval shortcuts ("+1 year") count from — the shot's date. */
  anchor: Date
  onTargetChange: (target: TreatmentReminderTarget | null) => void
  onDueAtChange: (dueAt: string | null) => void
}) {
  return (
    <>
      {options ? (
        <Select
          value={target ?? ''}
          onValueChange={(v) => onTargetChange((v || null) as TreatmentReminderTarget | null)}
          items={options.map((t) => ({ label: t, value: t }))}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Disease" />
          </SelectTrigger>
          <SelectContent>
            {options.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        // Fixed target: one vaccine maps to one disease, so there's nothing to choose.
        <span className="font-medium">{target}</span>
      )}
      <ReminderDueDateField anchor={anchor} value={dueAt} onChange={onDueAtChange} />
    </>
  )
}

/**
 * Picks a vaccine booster's due date: an "Interval" shortcut menu (each option
 * anchored to `anchor` and labelled with the date it resolves to) plus a calendar
 * for any custom date. A shortcut and a hand-picked date set the same `YYYY-MM-DD`
 * value, so the menu shows the matching interval when one applies, otherwise "Interval".
 */
function ReminderDueDateField({
  anchor,
  value,
  onChange,
}: {
  /** Calendar-date anchor for the interval shortcuts — the shot's date, or today. */
  anchor: Date
  /** Currently selected due date as a YYYY-MM-DD calendar string, or null. */
  value: string | null
  onChange: (dueAt: string) => void
}) {
  const fmt = useViewerFormat()
  // Each interval shortcut carries the concrete date it resolves to, so the vet sees
  // exactly what a shortcut produces before picking it.
  const intervalItems = REMINDER_INTERVALS.map((interval) => ({
    value: interval,
    label: `${REMINDER_INTERVAL_LABELS[interval]} · ${formatCalendarDate(computeReminderDueDate(anchor, interval), fmt)}`,
  }))
  // The Select is controlled, so its value must track the current date: show the matching
  // interval when the date equals one, otherwise the placeholder (a custom date was picked).
  const selectedInterval =
    REMINDER_INTERVALS.find((interval) => computeReminderDueDate(anchor, interval) === value) ?? ''

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Select
        value={selectedInterval}
        onValueChange={(interval) => {
          if (interval) onChange(computeReminderDueDate(anchor, interval as ReminderInterval))
        }}
        items={intervalItems}
      >
        <SelectTrigger className="w-40">
          <SelectValue placeholder="Interval" />
        </SelectTrigger>
        <SelectContent>
          {intervalItems.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <DatePicker
        value={value ? parseISO(value) : undefined}
        onChange={(date) => date && onChange(format(date, 'yyyy-MM-dd'))}
        placeholder="Custom date"
        // A booster can't fall due before the shot that triggers it.
        fromDate={anchor}
        className="w-auto"
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Interval presets — UI-only, which is why they live here rather than in
// `@snootsvet/shared`. The server stores only the resolved `dueAt` date and
// never sees the interval that produced it.
// ---------------------------------------------------------------------------

/** The vaccine booster intervals a vet can pick at administration time. */
const REMINDER_INTERVALS = [
  '2_weeks',
  '3_weeks',
  '4_weeks',
  '1_year',
  '3_years',
  '4_years',
] as const
type ReminderInterval = (typeof REMINDER_INTERVALS)[number]

/** Human-readable labels for each booster interval, for the interval picker. */
const REMINDER_INTERVAL_LABELS: Record<ReminderInterval, string> = {
  '2_weeks': '2 weeks',
  '3_weeks': '3 weeks',
  '4_weeks': '4 weeks',
  '1_year': '1 year',
  '3_years': '3 years',
  '4_years': '4 years',
}

/** Number of days to add for a week-based booster interval. */
const REMINDER_INTERVAL_DAYS: Partial<Record<ReminderInterval, number>> = {
  '2_weeks': 14,
  '3_weeks': 21,
  '4_weeks': 28,
}

/** Number of years to add for a year-based booster interval. */
const REMINDER_INTERVAL_YEARS: Partial<Record<ReminderInterval, number>> = {
  '1_year': 1,
  '3_years': 3,
  '4_years': 4,
}

/**
 * Computes the date a vaccine's next booster is due, given the administration
 * date and a chosen interval. Returns an ISO date string (`YYYY-MM-DD`) built
 * from local date parts to avoid timezone shifts.
 *
 * Week intervals add whole days; year intervals add calendar years. A Feb-29
 * dose + 1 year has no exact match, so JS Date normalizes it forward to Mar-1 —
 * acceptable for a reminder that only needs day precision.
 *
 * Exported for its unit test; the picker above is the only runtime caller.
 */
export function computeReminderDueDate(administeredAt: Date, interval: ReminderInterval): string {
  const due = new Date(administeredAt)
  const days = REMINDER_INTERVAL_DAYS[interval]
  const years = REMINDER_INTERVAL_YEARS[interval]
  if (days != null) due.setDate(due.getDate() + days)
  if (years != null) due.setFullYear(due.getFullYear() + years)
  const year = due.getFullYear()
  const month = String(due.getMonth() + 1).padStart(2, '0')
  const day = String(due.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
